import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import type { ValueDisplayPair, ValueSetResponse } from '@/types/settings-api';

/**
 * GET /api/Organization/OrganizationTypes?valueSetNames=OrganizationType
 * Lookup feeding the Account Details "Type of Practice" dropdown.
 *
 * The backend returns a nested wrapper:
 *   { valueSets: [ { entries: [ { code, display } ] } ] }
 * Flutter unwraps `valueSets[0].entries` into `[{ value: code, label: display }]`
 * (see `getOrganizationTypes` in settings_data_source_impl.dart). We do the
 * same on the way in so callers receive a flat array.
 */
export function useOrganizationTypes() {
  return useQuery({
    queryKey: ['settings', 'organization-types'],
    queryFn: async ({ signal }) => {
      const res = await get<ValueSetResponse>(
        '/api/Organization/OrganizationTypes?valueSetNames=OrganizationType',
        { signal },
      );
      const entries = res?.valueSets?.[0]?.entries ?? [];
      const out: ValueDisplayPair[] = entries.map((e) => ({
        value: e.code ?? null,
        label: e.display ?? null,
      }));
      return out;
    },
    staleTime: 1000 * 60 * 60,
  });
}
